/**
 * Entry-point rule for per-row designs: WHEN does the design icon appear in the
 * F-27 selection toolbar? PURE logic (no React, no DOM, no Yjs) — the caller
 * reads the board facts and hands them in, exactly like `translate/eligibility`.
 *
 * The rule mirrors translate's, because the shape of the operation is the same:
 * you select a target column and the Original Script column is the source. The
 * differences are that this one works on the SELECTED ROWS rather than the whole
 * column, and that it needs an art direction to exist first — a design run with
 * no art direction would spend real money rendering the house style alone,
 * which describes no video in particular.
 *
 * HIDDEN, not disabled, when ineligible — same reasoning as translate's: a
 * greyed icon invites the user to hunt for a setting that enables it, and there
 * is none. Narrowing the selection makes it appear; that is the discoverable
 * path. The ONE exception is "no art direction", which IS a state the user can
 * fix and would otherwise be a mystery — that shows the icon disabled with a
 * tooltip saying what to do.
 *
 * Nothing here is a security boundary: the route re-reads the source column,
 * the art direction and every row's text from the doc, and refuses a bad target
 * with a 400. This module's job is that the UI never OFFERS a run the server
 * would reject.
 */
import type { ToolbarSelection } from "../../registries/toolbar-actions/index.js";

/** Why the per-row design icon is withheld (each maps to a real state). */
export type RowDesignIneligibleReason =
  /** An element is selected — this works on grid rows. */
  | "not-cells"
  /**
   * The selection spans 0 or 2+ columns. One run puts one design on each row;
   * which of a multi-column selection it should land in would be a guess.
   */
  | "multi-column"
  /** No locked "Original Script" column — nothing to design FROM. */
  | "no-source-column"
  /**
   * The selection IS the source column. A design there would ratchet the script
   * column open around an image and bury the text F-28 protects.
   */
  | "source-column"
  /** More rows than one run may cover — the cost guard, surfaced early. */
  | "too-many-rows";

/** Board facts the rule needs. Read once by the caller. */
export interface RowDesignFacts {
  /** The sheet's locked F-28 source column, if it has one. */
  sourceColId: string | undefined;
  /** Has anyone written or generated an art direction yet? */
  hasArtDirection: boolean;
  /** Rows one run may cover. Mirrors the server's configured cap. */
  maxRows: number;
}

export type RowDesignEligibility =
  | { ok: true; colId: string; rowIds: readonly string[] }
  | { ok: false; reason: RowDesignIneligibleReason }
  /** Eligible in shape, but there is nothing to generate FROM yet. */
  | { ok: false; reason: "no-art-direction"; showDisabled: true };

/**
 * Decide whether the selection can become a per-row design run, and if so which
 * rows and column it targets.
 */
export function canDesignRows(
  selection: ToolbarSelection,
  facts: RowDesignFacts,
): RowDesignEligibility {
  if (selection.kind !== "cells") return { ok: false, reason: "not-cells" };
  if (selection.colIds.length !== 1) return { ok: false, reason: "multi-column" };

  const colId = selection.colIds[0];
  if (colId === undefined) return { ok: false, reason: "multi-column" };
  if (facts.sourceColId === undefined) return { ok: false, reason: "no-source-column" };
  if (colId === facts.sourceColId) return { ok: false, reason: "source-column" };

  if (selection.rowIds.length === 0) return { ok: false, reason: "multi-column" };
  if (selection.rowIds.length > facts.maxRows) return { ok: false, reason: "too-many-rows" };

  // Shape is fine — the only thing missing is the thing the user can go and
  // create, so this one is shown rather than hidden.
  if (!facts.hasArtDirection) {
    return { ok: false, reason: "no-art-direction", showDisabled: true };
  }

  return { ok: true, colId, rowIds: selection.rowIds };
}

/** Should the toolbar render the button at all? */
export function isRowDesignVisible(eligibility: RowDesignEligibility): boolean {
  return eligibility.ok || eligibility.reason === "no-art-direction";
}
